import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Screen, CommandBar } from '@/components/data';
import api from '@/lib/api';
import { downloadAuthedFile } from '@/lib/fileDownload';
import { formatApiError } from '@/lib/validators';
import { cn, istDayKey } from '@/lib/utils';
import { Save, Download, Eraser } from 'lucide-react';

import { STATUSES } from './documentTypes';
import { amountInWords, calcTotals, lineAmount, num } from './totals';
import {
  DocPage, DocFooter, DocBox, SectionHeading, MetaField, FieldLabel, BankDetails, DOC_FIELD,
} from './DocumentChrome';
import PartyPicker from './PartyPicker';
import LineItems from './LineItems';
import ChargesTable from './ChargesTable';
import SummaryPanel from './SummaryPanel';
import TermsEditor from './TermsEditor';

/**
 * One editor for both sales documents.
 *
 * The screen is the printed sheet, in the printed order, because that is the
 * document the office reads back to the customer over the phone. Every total on
 * it is computed as you type for the operator's benefit only — the server
 * recomputes the lot on save and its answer replaces what is on screen, so a
 * rounding difference or a stale rate can never be what gets stored.
 *
 * Rows carry a client-side `id` purely as a React key; the server owns the ids
 * of saved rows and hands them back.
 */

let rowCounter = 0;
const rowId = () => { rowCounter += 1; return `r${rowCounter}`; };

const blankLine = () => ({ id: rowId(), description: '', hsn_code: '', quantity: '', unit: 'pcs', rate: '' });
const blankCharge = () => ({ id: rowId(), particulars: '', amount: '', info: '' });

const addDays = (days) => istDayKey(new Date(Date.now() + days * 86400000));

function blankDoc(docType) {
  return {
    id: null,
    number: '',
    status: 'draft',
    customer_id: null,
    customer_name: '',
    date: istDayKey(new Date()),
    valid_until: addDays(30),
    reference_no: '',
    bill_to: { name: '', address: '', gstin: '', contact: '' },
    specs: {},
    // the printed sheet opens with three priced rows and one charge row each
    line_items: [blankLine(), blankLine(), blankLine()],
    plate_charges: [blankCharge()],
    die_charges: [blankCharge()],
    gst_rate: 18,
    freight: '',
    terms: [...docType.terms],
    notes: '',
  };
}

function fromServer(data, docType) {
  const rows = (list, blank) => (list?.length ? list.map((row) => ({ ...blank(), ...row, id: row.id || rowId() })) : []);
  return {
    ...blankDoc(docType),
    ...data,
    bill_to: { name: '', address: '', gstin: '', contact: '', ...(data.bill_to || {}) },
    specs: { ...(data.specs || {}) },
    line_items: rows(data.line_items, blankLine),
    plate_charges: rows(data.plate_charges, blankCharge),
    die_charges: rows(data.die_charges, blankCharge),
    terms: data.terms?.length ? [...data.terms] : [...docType.terms],
    freight: data.freight ?? '',
    notes: data.notes || '',
  };
}

export default function SalesDocumentEditor({ docType }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(() => blankDoc(docType));
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    if (!id) { setDoc(blankDoc(docType)); setLoading(false); return; }
    let live = true;
    setLoading(true);
    api.get(`${docType.api}/${id}`)
      .then((res) => { if (live) setDoc(fromServer(res.data, docType)); })
      .catch((err) => { if (live) toast.error(formatApiError(err, `Failed to load this ${docType.docTitle.toLowerCase()}`)); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [id, docType]);

  const set = useCallback((patch) => setDoc((prev) => ({ ...prev, ...patch })), []);
  const setNested = useCallback((key, patch) => {
    setDoc((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const editRows = useCallback((key, rowKey, patch) => {
    setDoc((prev) => ({
      ...prev,
      [key]: prev[key].map((row) => (row.id === rowKey ? { ...row, ...patch } : row)),
    }));
  }, []);
  const addRow = useCallback((key, blank) => {
    setDoc((prev) => ({ ...prev, [key]: [...prev[key], blank()] }));
  }, []);
  const removeRow = useCallback((key, rowKey) => {
    setDoc((prev) => ({ ...prev, [key]: prev[key].filter((row) => row.id !== rowKey) }));
  }, []);

  const totals = useMemo(() => calcTotals(doc), [doc]);
  const amountWords = useMemo(() => amountInWords(totals.grand_total), [totals.grand_total]);

  const pickCustomer = (customer) => set({
    customer_id: customer.id,
    customer_name: customer.customer_name,
    bill_to: {
      name: customer.firm_name || customer.customer_name || '',
      address: customer.billing_address || customer.delivery_address || '',
      gstin: customer.gstin || '',
      contact: [customer.customer_name, customer.mobile_number].filter(Boolean).join(' | '),
    },
  });

  const save = async () => {
    // a row nobody typed into is not a line the customer should be shown
    const filled = (row, ...fields) => fields.some((field) => String(row[field] ?? '').trim() !== '');
    const payload = {
      document_type: docType.key,
      status: doc.status,
      customer_id: doc.customer_id || null,
      customer_name: doc.customer_id ? doc.customer_name : (doc.bill_to.name || '').trim(),
      date: doc.date,
      valid_until: doc.valid_until,
      reference_no: docType.hasReference ? doc.reference_no || '' : '',
      bill_to: doc.bill_to,
      specs: doc.specs,
      line_items: doc.line_items
        .filter((row) => filled(row, 'description', 'quantity', 'rate'))
        .map((row) => ({
          description: row.description || '',
          hsn_code: row.hsn_code || '',
          quantity: num(row.quantity),
          unit: row.unit || '',
          rate: num(row.rate),
          amount: lineAmount(row),
        })),
      plate_charges: doc.plate_charges
        .filter((row) => filled(row, 'particulars', 'amount'))
        .map((row) => ({ particulars: row.particulars || '', amount: num(row.amount), info: row.info || '' })),
      die_charges: doc.die_charges
        .filter((row) => filled(row, 'particulars', 'amount'))
        .map((row) => ({ particulars: row.particulars || '', amount: num(row.amount), info: row.info || '' })),
      gst_rate: num(doc.gst_rate),
      freight: num(doc.freight),
      terms: doc.terms.map((term) => term.trim()).filter(Boolean),
      notes: doc.notes || '',
    };

    if (!payload.customer_name) {
      toast.error('Who is this for? Pick a customer or type the party name.');
      return;
    }

    setSaving(true);
    try {
      const res = doc.id
        ? await api.put(`${docType.api}/${doc.id}`, payload)
        : await api.post(docType.api, payload);
      setDoc(fromServer(res.data, docType));
      toast.success(`${docType.docTitle} ${res.data.number} saved`);
      if (!doc.id && res.data.id) navigate(`${docType.route}/${res.data.id}`, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err, `Failed to save this ${docType.docTitle.toLowerCase()}`));
    }
    setSaving(false);
  };

  const downloadPdf = async () => {
    if (!doc.id) { toast.error('Save it first — the PDF is built from the saved document'); return; }
    try {
      await downloadAuthedFile(`${docType.api}/${doc.id}/pdf`, `${doc.number || docType.docTitle}.pdf`);
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to download the PDF'));
    }
  };

  const clearAll = () => {
    setConfirmClear(false);
    // a saved document keeps its identity; only what was typed into it goes
    setDoc((prev) => ({ ...blankDoc(docType), id: prev.id, number: prev.number, status: prev.status }));
  };

  return (
    <Screen data-testid={`${docType.key}-editor`}>
      <CommandBar
        onBack={() => navigate(docType.route)}
        title={doc.number || `New ${docType.docTitle.toLowerCase()}`}
        subtitle={doc.customer_name || 'no customer yet'}
        actions={[
          { key: 'clear', label: 'Clear all', icon: Eraser, onClick: () => setConfirmClear(true) },
          {
            key: 'pdf', label: 'Download PDF', icon: Download, onClick: downloadPdf,
            disabled: !doc.id, testId: 'download-pdf-btn',
          },
          {
            key: 'save', label: saving ? 'Saving…' : 'Save', icon: Save, tone: 'primary', mnemonic: 's',
            onClick: save, disabled: saving || loading, testId: 'save-document-btn',
          },
        ]}
      />

      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-brand-blue" />
          </div>
        ) : (
          <DocPage title={docType.docTitle}>
            <div className="space-y-3 p-4">
              <div className={cn('grid gap-2 sm:grid-cols-2', docType.hasReference ? 'lg:grid-cols-5' : 'lg:grid-cols-4')}>
                <MetaField label={docType.numberLabel}>
                  <Input
                    value={doc.number || ''}
                    readOnly
                    placeholder={`${docType.numberPlaceholder} — on save`}
                    aria-label={docType.numberLabel}
                    data-testid="document-number"
                    className={`${DOC_FIELD} font-mono font-semibold text-doc-navy`}
                  />
                </MetaField>
                <MetaField label="Date">
                  <Input
                    type="date"
                    value={doc.date || ''}
                    onChange={(e) => set({ date: e.target.value })}
                    aria-label="Document date"
                    data-testid="document-date"
                    className={`${DOC_FIELD} font-mono text-doc-navy`}
                  />
                </MetaField>
                <MetaField label="Valid Until">
                  <Input
                    type="date"
                    value={doc.valid_until || ''}
                    onChange={(e) => set({ valid_until: e.target.value })}
                    aria-label="Valid until"
                    data-testid="document-valid-until"
                    className={`${DOC_FIELD} font-mono text-doc-navy`}
                  />
                </MetaField>
                {docType.hasReference && (
                  <MetaField label="Ref / Enquiry No.">
                    <Input
                      value={doc.reference_no || ''}
                      onChange={(e) => set({ reference_no: e.target.value })}
                      placeholder="ENQ-001"
                      aria-label="Reference or enquiry number"
                      data-testid="document-reference"
                      className={`${DOC_FIELD} font-mono text-doc-navy`}
                    />
                  </MetaField>
                )}
                <MetaField label="Status">
                  <select
                    value={doc.status}
                    onChange={(e) => set({ status: e.target.value })}
                    aria-label="Status"
                    data-testid="document-status"
                    className="h-7 w-full cursor-pointer bg-transparent px-1 text-sm text-doc-navy focus:outline-none focus:ring-1 focus:ring-inset focus:ring-brand-blue"
                  >
                    {STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </MetaField>
              </div>

              <div className="grid gap-2.5 lg:grid-cols-2">
                <PartyPicker
                  docType={docType}
                  customerId={doc.customer_id}
                  customerName={doc.customer_name}
                  billTo={doc.bill_to}
                  onPick={pickCustomer}
                  onUnlink={() => set({ customer_id: null })}
                  onChange={(patch) => setNested('bill_to', patch)}
                />
                <DocBox heading={docType.specsHeading} tone="teal">
                  <div className="p-2">
                    {docType.specFields.map(([field, label, placeholder]) => (
                      <label key={field} className="block">
                        <FieldLabel className="mt-1">{label}</FieldLabel>
                        <Input
                          value={doc.specs[field] ?? ''}
                          onChange={(e) => setNested('specs', { [field]: e.target.value })}
                          placeholder={placeholder}
                          aria-label={label}
                          data-testid={`spec-${field}`}
                          className={DOC_FIELD}
                        />
                      </label>
                    ))}
                  </div>
                </DocBox>
              </div>

              <LineItems
                heading={docType.itemsHeading}
                items={doc.line_items}
                onChange={(rowKey, patch) => editRows('line_items', rowKey, patch)}
                onAdd={() => addRow('line_items', blankLine)}
                onRemove={(rowKey) => removeRow('line_items', rowKey)}
              />

              <div className="grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_17rem]">
                <div>
                  <SectionHeading>{docType.chargesHeading}</SectionHeading>
                  <div className="space-y-2.5">
                    <ChargesTable
                      name="plate-charges"
                      heading="Plate Charges"
                      tone="teal"
                      rows={doc.plate_charges}
                      infoLabel={docType.chargeInfoLabel}
                      particularsPlaceholder="Plate making charges"
                      infoPlaceholder="No. of colours, size, material, supplier…"
                      onChange={(rowKey, patch) => editRows('plate_charges', rowKey, patch)}
                      onAdd={() => addRow('plate_charges', blankCharge)}
                      onRemove={(rowKey) => removeRow('plate_charges', rowKey)}
                    />
                    <ChargesTable
                      name="die-charges"
                      heading="Die / Cutting Charges"
                      tone="die"
                      rows={doc.die_charges}
                      infoLabel={docType.chargeInfoLabel}
                      particularsPlaceholder="Die making charges"
                      infoPlaceholder="Die size, type (rotary/flat), shape, supplier…"
                      onChange={(rowKey, patch) => editRows('die_charges', rowKey, patch)}
                      onAdd={() => addRow('die_charges', blankCharge)}
                      onRemove={(rowKey) => removeRow('die_charges', rowKey)}
                    />
                  </div>
                </div>
                <SummaryPanel
                  totals={totals}
                  gstRate={doc.gst_rate}
                  onGstRate={(value) => set({ gst_rate: value })}
                  freight={doc.freight}
                  onFreight={(value) => set({ freight: value })}
                  amountWords={amountWords}
                />
              </div>

              <div className="grid gap-2.5 lg:grid-cols-2">
                <TermsEditor
                  heading={docType.termsHeading}
                  terms={doc.terms}
                  onChange={(index, value) => set({ terms: doc.terms.map((t, i) => (i === index ? value : t)) })}
                  onAdd={() => set({ terms: [...doc.terms, ''] })}
                  onRemove={(index) => set({ terms: doc.terms.filter((_, i) => i !== index) })}
                />
                <BankDetails />
              </div>

              <div>
                <SectionHeading>{docType.notesHeading}</SectionHeading>
                <Textarea
                  rows={2}
                  value={doc.notes || ''}
                  onChange={(e) => set({ notes: e.target.value })}
                  placeholder={docType.notesPlaceholder}
                  aria-label={docType.notesHeading}
                  data-testid="document-notes"
                  className="resize-none border-doc-rule text-sm shadow-none"
                />
              </div>
            </div>
            <DocFooter note={docType.footerNote} />
          </DocPage>
        )}
      </div>

      <AlertDialog open={confirmClear} onOpenChange={setConfirmClear}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear every field?</AlertDialogTitle>
            <AlertDialogDescription>
              The party, the specifications, the rows and the charges all go back to blank.
              Nothing is saved until you press Save.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={clearAll} className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-clear-all">
              Clear all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Screen>
  );
}
